import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React, {
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
  memo,
} from 'react'

export interface Props {
  disabled?: boolean
  name: string
  type?: string
  placeholder?: string
  error?: boolean
  isDirty?: boolean
  required?: boolean
  variant?: Variant
  value?: string
  onBlur?: FocusEventHandler<HTMLInputElement>
  onChange?: ChangeEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
}

export const Input = memo(
  forwardRef<HTMLInputElement, Props>(function Input(
    {
      disabled,
      name,
      type,
      placeholder,
      error = false,
      isDirty = false,
      required,
      variant = 'text',
      value,
      onBlur,
      onChange,
      onFocus,
    },
    ref,
  ) {
    return (
      <Container
        ref={ref}
        error={error}
        isDirty={isDirty}
        disabled={disabled}
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        variant={variant}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
      />
    )
  }),
)

Input.displayName = 'Input'

const Container = styled.input<ContainerProps>`
  ${({ theme, error, variant }) => {
    return css`
      font-family: ${theme.fontFamily.paragraph};

      :-webkit-autofill,
      :-webkit-autofill:hover,
      :-webkit-autofill:focus,
      :-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 100px black inset;
        -webkit-text-fill-color: white;
      }

      ${variant === 'submit'
        ? css`
            height: 3.25rem;
            font-size: 0.8125rem;
            font-weight: 700;
            background-color: ${theme.colors.variants.primaryDark1};
            color: ${theme.colors.variants.neutralLight4};
            cursor: pointer;
            letter-spacing: 1px;
            margin: 50px auto 0 auto;
            padding: 0 1.875rem;
            text-align: center;
            text-transform: uppercase;
            transition: 0.3s;
            &:hover {
              background-color: ${theme.colors.getHoverColor(
                theme.colors.variants.primaryDark1,
              )};
            }

            &:disabled {
              background-color: ${theme.colors.variants.neutralDark2};
              cursor: not-allowed;
            }
          `
        : css`
            width: 100%;
            height: 2.25rem;
            font-size: 0.875rem;
            color: ${theme.colors.variants.neutralDark1};
            border-bottom: 2px solid
              ${error
                ? theme.colors.variants.dangerLight1
                : theme.colors.variants.neutralLight2};
          `}
    `
  }}
`

interface ContainerProps {
  error: boolean
  isDirty: boolean
  variant: Variant
}

export type Variant = 'text' | 'checkbox' | 'submit'
